// Global Instructions Rule Applied!
import { FieldConfig } from '../../../core/components/InputManager/types.ts'
import { isRequiredFieldValid } from '../../../core/components/InputManager/utils/formatters.ts'
import { DealFields } from '../../../state_manager/data_models/deal_record.ts'

/**
 * Validates required fields for deal form
 * @param fields - Array of field configurations
 * @param formValues - Current form values from Ant Design Form
 * @param formData - Current form data state
 * @param selectedContacts - Selected contacts for deal team fields (optional)
 * @returns Object with isValid flag and errors object
 */
export function validateRequiredFields(
  fields: FieldConfig[],
  formValues: Record<string, any>,
  formData: Record<string, any>,
  selectedContacts?: Record<string, any>
): { isValid: boolean; errors: Record<string, string> } {
  const errors: Record<string, string> = {}

  fields.forEach((field) => {
    if (!field.required) return

    const fieldKey = field.key
    let value = formValues[fieldKey] ?? formData[fieldKey]

    // Handle contact fields that are stored in selectedContacts
    // Support both key formats: DealFields constants or simplified keys
    if (fieldKey === DealFields.contact_accounts__introduced_by) {
      value = selectedContacts?.[fieldKey] ?? selectedContacts?.introduced_by ?? value
    } else if (fieldKey === DealFields.contact_accounts__sourced_by) {
      value = selectedContacts?.[fieldKey] ?? selectedContacts?.sourced_by ?? value
    } else if (fieldKey === DealFields.contact_deals__lead) {
      value = selectedContacts?.[fieldKey] ?? selectedContacts?.deal_team_lead ?? value
    }

    // Handle conditional requirements
    if (fieldKey === DealFields.deals__account) {
      // Company is only required if not creating a new account
      if (formData[DealFields.prog__is_new_account]) {
        return // Skip validation for company field when creating new account
      }
    }

    if (fieldKey === DealFields.accounts__name) {
      // Company name is only required if creating a new account
      if (!formData[DealFields.prog__is_new_account]) {
        return // Skip validation for company name when using existing company
      }
    }

    // Validate the field value
    if (!isRequiredFieldValid(value)) {
      const fieldLabel = field.label || fieldKey
      errors[fieldKey] = `${fieldLabel} is required`
    }
  })

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  }
}
